import {
    Box,
    Checkbox,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useKeycloak } from '@react-keycloak/web';
import { useQueryClient } from '@tanstack/react-query';
import { Field, Form, Formik, useFormikContext } from "formik";
import { useAtom, useAtomValue } from 'jotai';
import * as React from 'react';
import { useEffect } from 'react';
import { baseAtom, getIsEntryLinkedToEntry } from '../../stores/jotai/base';
import { canvasAtom } from '../../stores/jotai/canvas/canvas';
import { canvasDataAtom } from '../../stores/jotai/canvas/canvasData';
import { hasPermissionCanvasWrite } from '../../stores/jotai/permissions';
import { useCreateLinkSet } from '../../tanstackQuery/queries/canvas/useCreateLinkSet';
import { highlightLinkedEntries, removeHighlightFromEntries } from '../../util/canvasEntryStyleManipulation';
import OverlayTitle from '../generics/components/overlay-title';
import CanvasEntryRefContext from './contexts/CanvasEntryRefContext';
import { sortEntries } from '../../util/entries';

const CustomCheckbox = ({ item }) => {
    const { values, setFieldValue } = useFormikContext();

    return (
        <Checkbox
            className='mcila-checkbox'
            checked={values.selectedItems.includes(item)}
        />
    );
};

const componentSpecificKeyExtender = (key) => {
    let _key = "";

    if (typeof key === "string") { _key = key; }
    else { _key = key.toString(); }

    return `material-canvas-insight-link-ai-${_key}`;
}

const FilteredList = ({ items, initialValues, canEdit }) => {
    const { values, setFieldValue, submitForm } = useFormikContext();
    const { canvasEntryRefCollection } = React.useContext(CanvasEntryRefContext);
    const { selectedHypothesisIDToFilter } = useAtomValue(canvasAtom);
    const theme = useTheme();
    const isChecked = (item) => {
        if (values.selectedItems) {
            return values.selectedItems.includes(item.value);
        }
        else {
            return false;
        }
    };

    useEffect(() => {
        try {
            submitForm()
        } catch (e) { }
    }, [
        values
    ])

    const handleChange = (item) => {
        if (canEdit) {
            if (values?.selectedItems === undefined) { }
            else {
                if (values?.selectedItems?.includes(item)) {
                    setFieldValue(
                        "selectedItems",
                        values?.selectedItems?.filter((i) => i !== item)
                    );
                } else {
                    setFieldValue("selectedItems", [...values.selectedItems, item]);
                }
            }
        }
    };


    const itemSizeStyle = { lineHeight: '24px', fontSize: '24px' }

    return (
        <List
            sx={{
                maxHeight: "300px",
                overflowY: "scroll",
                overflowX: "clip",
                "& .mcila-list-item-checked": {
                    backgroundColor: theme?.custom?.mainButtonHighlight,
                    color: "white !important",
                    "& .mcila-checkbox svg": {
                        fill: "white !important",
                    },
                },
            }}
        >
            {items.map((item) => {
                const csEntryID = item?.value;
                const htmlEntryElement = canvasEntryRefCollection[csEntryID];
                const selectedCSHtmlEntryElements = values?.selectedItems?.map(
                    (entryID) => canvasEntryRefCollection[entryID]
                );

                return <ListItem
                    sx={{
                        ...itemSizeStyle,
                        padding: "5px",
                        margin: "0px",
                        ":hover": {
                            backgroundColor: !isChecked(item)
                                ? theme?.custom?.hoverOptionColor
                                : null,
                            cursor: "pointer",
                            pointerEvents: "all",
                        },
                    }}
                    className={isChecked(item) ? "mcila-list-item-checked" : ""}
                    key={componentSpecificKeyExtender(item?.value)}
                    onTouchStart={() => {
                        handleChange(item?.value);
                    }}
                    onClick={() => {
                        handleChange(item?.value);
                    }}
                    onMouseEnter={(e) => {
                        if (!selectedHypothesisIDToFilter) {
                            highlightLinkedEntries({
                                entriesToHighlight: [
                                    htmlEntryElement,
                                    ...selectedCSHtmlEntryElements,
                                ],
                                canvasEntryRefCollection,
                            });
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!selectedHypothesisIDToFilter) {
                            removeHighlightFromEntries({
                                canvasEntryRefCollection,
                                entryIDsToSkip: values?.selectedItems,
                                theme
                            });
                        }
                    }}
                >
                    <ListItemIcon
                        sx={{
                            ...itemSizeStyle,
                            height: "24px",
                        }}
                    >
                        <Field
                            id={componentSpecificKeyExtender(item?.value)}
                            component={CustomCheckbox}
                            item={item?.value}
                        />
                    </ListItemIcon>
                    <ListItemText
                        sx={itemSizeStyle}
                        primary={item?.label}
                    />
                </ListItem>})
            }
        </List>
    );
};

const VerticalList = ({ menu, onCancel, search, onChange, canEdit,canvasType }) => {
    const initialValues = {
        search: "",
        selectedItems: menu?.filter(item => item.isSelected).map(item => item.value),
    };

    const handleFilter = (items, search) => {
        return items?.filter((item) =>
            item?.label?.toLowerCase().includes(search.toLowerCase())
        );
    };

    const searchWidget = search ? <Field
        name="search"
        as={TextField}
        fullWidth
        margin="none"
        placeholder="Search..."
    /> : null;

    return (
        <Formik onSubmit={(values) => {
            if (onChange) {
                onChange({
                    menu,
                    values
                });
            }
        }} initialValues={initialValues}>
            {({ values }) => (
                <Box sx={{
                    '& .MuiInputBase-formControl': {
                        borderRadius: '0px'
                    }
                }}>
                    <Form>
                        <OverlayTitle onClose={onCancel} title={canvasType==="DThreeCanvas"  ? "Customer Segments and Stakeholders" : "Link to Customer Segments"} />
                        {searchWidget}
                        <FilteredList
                            canEdit={canEdit}
                            items={handleFilter(menu, values?.search)} initialValues={initialValues}
                        />
                    </Form>
                </Box>
            )}
        </Formik>
    );
};

export default function MaterialCanvasInsightLinkAI(props: any) {
    const entryID = props?.entryID;
    const [base, setBase] = useAtom(baseAtom);
    const [canvasData,setCanvasData]=useAtom(canvasDataAtom);
    const [lastLocalMenuValues, setLastLocalMenuValues] = React.useState(null);
    const { canvasEntryRefCollection } = React.useContext(
        CanvasEntryRefContext
    );
    const { selectedHypothesisIDToFilter } = useAtomValue(canvasAtom);
    const theme = useTheme();
    const { keycloak } = useKeycloak()
    const client = useQueryClient()
    const { mutateAsync: createLinkSet } = useCreateLinkSet(
      { accessToken: keycloak?.token },
      { onSuccess() {
        client.invalidateQueries({ queryKey: ["canvas"] })
      } }
    );
    
    const canEdit = hasPermissionCanvasWrite(base);

    const generateMenuConfig = (data) => {
        let filteredData = data;

        if (!canEdit) {
            filteredData = filteredData?.filter((item) =>
                getIsEntryLinkedToEntry(canvasData, entryID, item?.entryID)
            );
        }

        return filteredData?.map((item) => {
            return {
                label: item?.entry,
                value: item?.entryID,
                isSelected: getIsEntryLinkedToEntry(canvasData, entryID, item?.entryID), // Set isSelected to false by default, update as needed
            };
        });
    };

    const menu = generateMenuConfig(sortEntries(props?.csEntries));

    useEffect(() => {
        if (!selectedHypothesisIDToFilter) {
            if (base?.canvasLinkTarget) {
                removeHighlightFromEntries({
                    canvasEntryRefCollection,
                    entryIDsToSkip: menu
                        ?.filter((item) => item?.isSelected)
                        .map((item) => item?.value),
                        theme
                });
            } else {
                removeHighlightFromEntries({ canvasEntryRefCollection,theme });
            }
        }

        return () => {
            if (!selectedHypothesisIDToFilter) {
                removeHighlightFromEntries({ canvasEntryRefCollection,theme });
            }
        }
    }, [base?.canvasLinkTarget]);

    return <Box sx={{ width: '425px', background: 'white', border: '0px', borderRadius: '4px', boxShadow: theme?.custom?.menuBoxShadow, padding: '0px', margin: '0px' }}>
        <VerticalList menu={menu}
            search={false}
            canEdit={canEdit}
            onChange={(newMenu) => {
                if (lastLocalMenuValues === null) {
                    setLastLocalMenuValues(newMenu.values);
                }
              
                else {
                    const payload = {
                        groupID: base?.team?.groupID,
                        entryID: entryID,
                        targetEntryID:newMenu.values.selectedItems.join(",")
                      }
                    createLinkSet(payload).then((res) => {
                          if(res.done === true) {
                            setCanvasData((canvasData) => {
                                const buffer = { ...canvasData }
                                payload.targetEntryID = newMenu?.values?.selectedItems[newMenu?.values?.selectedItems?.length - 1];
                                buffer.linkSet.push(payload);
                                return buffer;
                            });
                          }

                      });
                }
            }}
            onCancel={() => {
                props.close();
            }}
            canvasType={canvasData?.type}
        />
    </Box>
}
